import type { Account } from "../api/types";

interface Props {
  accounts: Account[];
  selectedId: number | null;
  onSelect: (id: number | null) => void;
  showRetired?: boolean;
  /** When true, shows "All accounts" option at the top of the dropdown. */
  showAll?: boolean;
}

/** Map account kind to a CSS class for the badge. */
function kindBadgeCls(kind: string): string {
  switch (kind) {
    case "sim": return "pill kind";
    case "rithmic_direct": return "pill good";
    case "tradovate": return "pill";
    case "sierra_bridged": return "pill dim";
    case "topstepx": return "pill dim";
    default: return "pill dim";
  }
}

/** Human-readable label for an account kind. */
function kindLabel(kind: string): string {
  switch (kind) {
    case "sim": return "sim";
    case "rithmic_direct": return "rithmic";
    case "tradovate": return "tradovate";
    case "sierra_bridged": return "sierra";
    case "topstepx": return "topstep";
    default: return kind;
  }
}

function assetLabel(asset?: string | null): string {
  return asset ? asset.replace("multi_asset", "multi-asset") : "scope unset";
}

export function AccountPicker({ accounts, selectedId, onSelect, showRetired, showAll }: Props) {
  // Filter retired accounts unless the toggle is on
  const visible = showRetired ? accounts : accounts.filter((a) => !a.retired);
  if (visible.length === 0) return null;

  if (visible.length === 1 && !showAll) {
    const a = visible[0]!;
    return (
      <span className="muted small">
        <span className={kindBadgeCls(a.kind)} style={{ marginRight: 4 }}>{kindLabel(a.kind)}</span>
        {a.name} <span className="mono">#{a.id}</span> <span className="muted">[{assetLabel(a.asset_class)} · {a.venue ?? "venue unset"}]</span>
        {a.retired && <span className="pill dim" style={{ marginLeft: 4 }}>retired</span>}
      </span>
    );
  }
  return (
    <select
      value={selectedId != null ? selectedId : "ALL"}
      onChange={(e) => {
        const v = e.target.value;
        onSelect(v === "ALL" ? null : Number(v));
      }}
      style={{ maxWidth: 220 }}
    >
      {showAll && (
        <option value="ALL">All accounts</option>
      )}
      {visible.map((a) => (
        <option key={a.id} value={a.id}>
          {a.name} [{kindLabel(a.kind)} · {assetLabel(a.asset_class)} · {a.venue ?? "venue unset"}] #{a.id}{a.retired ? " (retired)" : ""}
        </option>
      ))}
    </select>
  );
}